import { ThunkDispatch } from "@reduxjs/toolkit";
import { connect, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { itemRemovalActions, ItemImage } from '../../reducers/itemRemoval';
import { selectCanProcessItemRemoval, selectCanRegenProcessedImages } from '../../selectors';
import AlertModal from "../common/AlertModal";
import ConfirmModal from "../common/ConfirmModal";
import { ConfirmGenerateAfterPhotoIcon } from '../SVGIcons';

// State型定義
interface RootState {
  itemRemoval: {
    itemImages: ItemImage[];
    isConfirmPopupShow: boolean;
    isRegenPopupShow: boolean;
    isPhotoTooBigPopupShow: boolean;
    isClearAllPopupShow: boolean;
    selectedItemImage?: number;
    [key: string]: unknown;
  };
  currentUser: {
    creditConsumed: number;
    creditLimit: number;
    isLimitInfinity: boolean;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

// Propsの型定義
interface ItemRemovalModalsProps {
  itemImages: ItemImage[];
  isConfirmPopupShow: boolean;
  isRegenPopupShow: boolean;
  isPhotoTooBigPopupShow: boolean;
  isClearAllPopupShow: boolean;
  canProcessItemRemoval: boolean;
  canRegenAfterImages: boolean;
  creditConsumed: number;
  creditLimit: number;
  isLimitInfinity: boolean;
  selectedItemImageData?: ItemImage;
  unprocessedCount: number;  // 未処理の画像数
}

// mapStateToPropsの型定義
function mapStateToProps(state: RootState): ItemRemovalModalsProps {
  const canProcessItemRemoval = selectCanProcessItemRemoval(state);
  const canRegenAfterImages = selectCanRegenProcessedImages(state);

  // 直接itemImagesから選択された画像を取得
  const { itemImages, selectedItemImage } = state.itemRemoval;
  const selectedItemImageData = selectedItemImage
    ? itemImages.find((img: any) => img.stubId === selectedItemImage)
    : (itemImages.length > 0 ? itemImages[0] : null);

  // 未処理の画像数を計算（実際に処理される画像数）
  const unprocessedCount = itemImages.filter((img: ItemImage) => !img.isProcessed).length;

  return {
    ...state.itemRemoval,
    ...state.currentUser,
    canProcessItemRemoval,
    canRegenAfterImages,
    selectedItemImageData,
    unprocessedCount,
  };
}

const ItemRemovalModals = (props: ItemRemovalModalsProps) => {
  const {
    itemImages,
    isConfirmPopupShow,
    isRegenPopupShow,
    isPhotoTooBigPopupShow,
    isClearAllPopupShow,
    canProcessItemRemoval,
    canRegenAfterImages,
    creditConsumed,
    creditLimit,
    isLimitInfinity,
    selectedItemImageData,
    unprocessedCount
  } = props;

  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const navigate = useNavigate();

  // 共通処理
  const closeConfirmPopup = () => {
    dispatch(itemRemovalActions.setIsConfirmPopupShow(false));
  };

  const closeRegenPopup = () => {
    dispatch(itemRemovalActions.setIsRegenPopupShow(false));
  };

  const closePhotoTooBigPopup = () => {
    dispatch(itemRemovalActions.setIsPhotoTooBigPopupShow(false));
  };

  const closeClearAllPopup = () => {
    dispatch(itemRemovalActions.setIsClearAllPopupShow(false));
  };

  // アクション処理
  const processItemRemoval = () => {
    if (!canProcessItemRemoval) return;

    // 再実行の場合は、isProcessedFailの状態をリセット
    if (selectedItemImageData && selectedItemImageData.isProcessedFail) {
      const newItemImages = itemImages.map((img: any) => {
        if (img.stubId === selectedItemImageData.stubId) {
          return {
            ...img,
            isProcessedFail: false
          };
        }
        return img;
      });
      dispatch(itemRemovalActions.setItemImages(newItemImages));
    }

    dispatch(itemRemovalActions.setIsConfirmPopupShow(false));
    dispatch(itemRemovalActions.processItemRemoval(navigate));
  };

  const regenAfterImages = () => {
    // まずモーダルを閉じる
    dispatch(itemRemovalActions.setIsRegenPopupShow(false));

    // selectedItemImageData.idを使って直接APIを呼び出す
    dispatch(itemRemovalActions.regenSelectedAfterImage());
  };

  const clearAll = () => {
    dispatch(itemRemovalActions.clearData());
  };

  // 実行回数表示用
  const consumedRatio = isLimitInfinity ? '0%' : `${(creditConsumed / creditLimit) * 100}%`;

  return (
    <>
      {/* 家具消し実行確認モーダル */}
      <ConfirmModal
        show={isConfirmPopupShow}
        onClose={closeConfirmPopup}
        onConfirm={processItemRemoval}
        icon={<ConfirmGenerateAfterPhotoIcon />}
        title={`家具消しに${unprocessedCount}回分のクレジットを使用します。よろしいですか？`}
        confirmText="家具消し"
      >
        <div className="mt-2" />
        <div className="flex justify-between mb-1 text-gray-500">
          <span className="text-base font-normal">実行回数</span>
          <span className="text-sm font-semibold text-gray-900">
            {isLimitInfinity
              ? `実行回数 ${creditConsumed}回/上限なし(アカウントのクレジット上限)`
              : `残り${creditLimit - creditConsumed}回/${creditLimit}回`
            }
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div
            className="bg-orange-500 h-2.5 rounded-full"
            style={{ width: consumedRatio }}
          />
        </div>
      </ConfirmModal>

      {/* 家具消し再実行確認モーダル */}
      <ConfirmModal
        show={isRegenPopupShow}
        onClose={closeRegenPopup}
        onConfirm={regenAfterImages}
        icon={<ConfirmGenerateAfterPhotoIcon />}
        title="再実行すると新たに1枚の画像が追加されます。よろしいでしょうか。再実行では実行回数は消費されません。"
        confirmText="OK"
      />

      {/* 画像サイズエラーモーダル */}
      <AlertModal
        show={isPhotoTooBigPopupShow}
        onClose={closePhotoTooBigPopup}
        title="5M以上のサイズの画像はアップロードできません。"
      />

      {/* 全てクリア確認モーダル */}
      <ConfirmModal
        show={isClearAllPopupShow}
        onClose={closeClearAllPopup}
        onConfirm={clearAll}
        icon={<ConfirmGenerateAfterPhotoIcon />}
        title="初期画面に戻るため、生成した画像がダウンロードできませんがよろしいでしょうか？"
      />
    </>
  );
};

export default connect(mapStateToProps)(ItemRemovalModals);